import { css, useTheme } from '@emotion/react';
import { DrawerMenu, Icon, parseMenuDataFromContentfulMegaNav } from 'common-ui';
import React, { memo, useMemo, useState } from 'react';
import type {
  ArrayEntryType,
  ConditionalContainerEntryType,
  DLClickEventHandlerProps,
  LinkEntryType,
  SiteEntryType,
  TabEntryType,
  TabsEntryType,
} from 'tsconfig/types';
import RenderLevel1Menu from './RenderLevel1Menu';
import RenderLevel2Menu from './RenderLevel2Menu';
import { renderSocialLinks, renderFooterNav, renderBadges } from './MainNavMobile.utils';
import { TypeBasedConditionalContent } from '../../../contentful/CmsConditionalContainer/TypeBasedConditionalContent';

export type MainNavMobileProps = {
  cmsMainNav?: ArrayEntryType<TabsEntryType>;
  footerNavBar?: SiteEntryType['fields']['footerNavBar'];
  socialLinks?: ArrayEntryType<LinkEntryType>;
  badgeImages?: ArrayEntryType<LinkEntryType>;
  dlClickEventHandler: DLClickEventHandlerProps;
};

export const MainNavMobile = memo(
  ({ cmsMainNav = [], footerNavBar, socialLinks, badgeImages, dlClickEventHandler }: MainNavMobileProps) => {
    const [showLevel2Menu, setShowLevel2Menu] = useState(false);
    const [currentLevel2MenuContent, setCurrentLevel2MenuContent] = useState<string>();
    const [stickyContent, setStickyContent] = useState<LinkEntryType[]>();
    const theme = useTheme();
    const getLevel1MenuLeft = (show: boolean) => (show ? '-100%' : '0');
    const getLevel2And3MenuLeft = (show: boolean) => (show ? '0' : '100%');
    const menuContainer = css`
        ${theme.widgets.DrawerMenu?.mobileNavMenuContainer}
      `,
      level1MenuPane = css`
        left: ${getLevel1MenuLeft(showLevel2Menu)};
      `,
      level2And3MenuPane = css`
        left: ${getLevel2And3MenuLeft(showLevel2Menu)};
      `;

    const backArrow = () => {
      setShowLevel2Menu(false);
      setCurrentLevel2MenuContent(undefined);
    };

    function onShowLevel2Menu(chosenLevel2Menu: string) {
      setShowLevel2Menu(true);
      setCurrentLevel2MenuContent(chosenLevel2Menu);
    }

    const renderLevel1Menu = (fields: any) => (
      <RenderLevel1Menu
        fields={fields}
        setStickyContent={setStickyContent}
        dlClickEventHandler={dlClickEventHandler}
        cb={(val: string) => onShowLevel2Menu(val)}
      />
    );

    function onCloseDrawer() {
      //reset menu to root level when closed
      setShowLevel2Menu(false);
      setCurrentLevel2MenuContent(undefined);
    }

    const renderTabsConditionalContainerContent = (tabCCItem: ConditionalContainerEntryType) =>
      tabCCItem?.fields?.content?.find(content => content?.sys?.id === currentLevel2MenuContent);

    const renderConditionalContainerTabsContent = (tabCCItem: TabsEntryType) =>
      tabCCItem?.fields?.tabPanes?.find(tabPane => tabPane?.sys?.id === currentLevel2MenuContent);

    function isTabsEntry(entry: any): entry is TabsEntryType {
      return entry?.sys?.contentType?.sys?.id === 'tabs';
    }

    function isConditionalContainer(entry: any): entry is ConditionalContainerEntryType {
      return entry?.sys?.contentType?.sys?.id === 'conditionalContainer';
    }

    function findTabPaneContent(tabPanes: (TabEntryType | ConditionalContainerEntryType)[]) {
      for (const tabItem of tabPanes) {
        if (isConditionalContainer(tabItem)) {
          const ccContent = renderTabsConditionalContainerContent(tabItem);
          if (ccContent) {
            return ccContent;
          }
        }
        if (tabItem?.sys?.id === currentLevel2MenuContent) {
          return tabItem;
        }
      }
      return null;
    }

    function findContentInConditionalContainer(container: ConditionalContainerEntryType) {
      for (const item of container?.fields?.content || []) {
        if (isTabsEntry(item)) {
          const tabsContent = renderConditionalContainerTabsContent(item);
          if (tabsContent) {
            return tabsContent;
          }
        }
        if (item?.sys?.id === currentLevel2MenuContent) {
          return item;
        }
      }
      return null;
    }

    function handleTabsEntry(entry: TabsEntryType) {
      return findTabPaneContent((entry.fields?.tabPanes as TabEntryType[]) ?? []);
    }

    function handleConditionalContainer(entry: ConditionalContainerEntryType) {
      return findContentInConditionalContainer(entry);
    }

    const typeHandlers: Record<string, (item: any) => any> = {
      tabs: handleTabsEntry,
      conditionalContainer: handleConditionalContainer,
    };

    function findContentInMainNav(items: (TabsEntryType | ConditionalContainerEntryType)[]) {
      for (const item of items) {
        if (!item) {continue;}

        if (item.sys?.id === currentLevel2MenuContent) {return item;}

        const typeId = item.sys?.contentType?.sys?.id;
        const handler = typeHandlers[typeId ?? ''];

        if (handler) {
          const result = handler(item);
          if (result) {return result;}
        }
      }

      return null;
    }

    function renderLevel2MenuContentData() {
      const filteredLvl2Content = findContentInMainNav(cmsMainNav as (TabsEntryType | ConditionalContainerEntryType)[]);

      if (!filteredLvl2Content) {
        return null;
      }

      const menuData = parseMenuDataFromContentfulMegaNav(filteredLvl2Content as TabEntryType);

      return <RenderLevel2Menu menuItems={menuData} onBack={backArrow} dlClickEventHandler={dlClickEventHandler} />;
    }

    //Render the tabs
    const renderMegaNavTabs = useMemo(
      () =>
        (cmsMainNav as TabsEntryType[])?.map((tabGroup: TabsEntryType) => {
          const {
            fields: { entryTitle },
          } = tabGroup;

          return (
            <TypeBasedConditionalContent<TabsEntryType>
              content={tabGroup}
              contentTypes={['tabs']}
              key={entryTitle}
              fallback={(content: TabsEntryType) => renderLevel1Menu(content?.fields)}
            >
              <div>{renderLevel1Menu(tabGroup?.fields)}</div>
            </TypeBasedConditionalContent>
          );
        }),
      [cmsMainNav],
    );

    return (
      <div data-test="widget-MainNavMobile" className="header-mainnav-mobile">
        <DrawerMenu
          position="left"
          target={<Icon kind="hamburger-menu" size="l" />}
          onClose={onCloseDrawer}
          className="navigation-link"
          isAccessibleFor="notDesktop"
          stickyContent={stickyContent}
        >
          <div css={menuContainer}>
            <div css={level1MenuPane} className="level1MenuPane">
              <div className="menu-container">{renderMegaNavTabs}</div>
              <div>
                {renderSocialLinks(socialLinks?.filter((link): link is LinkEntryType => !!link))}
                {renderFooterNav(footerNavBar)}
                {renderBadges(badgeImages?.filter((badge): badge is LinkEntryType => !!badge))}
              </div>
            </div>
            <div css={level2And3MenuPane} className="level2And3MenuPane">
              <div>{currentLevel2MenuContent && renderLevel2MenuContentData()}</div>
            </div>
          </div>
        </DrawerMenu>
      </div>
    );
  },
);
